import BigNumber from 'bignumber.js';

import type { ApiLiquidityHub, LiquidityHub, Token, VhToken } from 'types';
import { areAddressesEqual } from 'utilities/areAddressesEqual';
import { calculateYearlyInterests } from 'utilities/calculateYearlyEarnings';
import { convertMantissaToTokens } from 'utilities/convertMantissaToTokens';
import convertUsdMantissaToCents from 'utilities/convertUsdMantissaToCents';
import { formatApiRewardDistributors } from 'utilities/formatApiRewardDistributors';
import getCombinedApy from 'utilities/getCombinedApy';
import { formatToLiquidityHubYieldGroup } from './formatToLiquidityHubYieldGroup';

export const formatToLiquidityHub = ({
  apiLiquidityHub,
  tokens,
}: {
  apiLiquidityHub: ApiLiquidityHub;
  tokens: Token[];
}): LiquidityHub | undefined => {
  const underlyingToken = tokens.find(token =>
    areAddressesEqual(token.address, apiLiquidityHub.underlyingTokenAddress),
  );

  if (!underlyingToken) {
    return;
  }

  const vhToken: VhToken = {
    address: apiLiquidityHub.hubAddress,
    chainId: underlyingToken.chainId,
    decimals: apiLiquidityHub.hubTokenDecimals,
    symbol: apiLiquidityHub.symbol,
    underlyingToken,
  };

  const supplyBalanceTokens = convertMantissaToTokens({
    value: new BigNumber(apiLiquidityHub.totalUnderlyingMantissa),
    token: underlyingToken,
  });
  const supplyBalanceCentsFromApi = convertUsdMantissaToCents(
    apiLiquidityHub.totalUnderlyingUsdMantissa ?? 0,
  );
  const tokenPriceCents = supplyBalanceTokens.isZero()
    ? new BigNumber(0)
    : supplyBalanceCentsFromApi.dividedBy(supplyBalanceTokens);

  const supplyBalanceCents = apiLiquidityHub.totalUnderlyingUsdMantissa
    ? supplyBalanceCentsFromApi
    : supplyBalanceTokens.multipliedBy(tokenPriceCents);

  const liquidityTokens = convertMantissaToTokens({
    value: new BigNumber(apiLiquidityHub.liquidityMantissa),
    token: underlyingToken,
  });

  const liquidityCentsFromApi = convertUsdMantissaToCents(
    apiLiquidityHub.liquidityUsdMantissa ?? 0,
  );

  const liquidityCents = apiLiquidityHub.liquidityUsdMantissa
    ? liquidityCentsFromApi
    : liquidityTokens.multipliedBy(tokenPriceCents);

  const withdrawalCapTokens = convertMantissaToTokens({
    value: new BigNumber(apiLiquidityHub.maxWithdrawalSizeMantissa),
    token: underlyingToken,
  });

  const userSupplyBalanceTokens = apiLiquidityHub.userUnderlyingBalanceMantissa
    ? convertMantissaToTokens({
        value: new BigNumber(apiLiquidityHub.userUnderlyingBalanceMantissa),
        token: underlyingToken,
      })
    : undefined;

  const userWalletBalanceTokens = apiLiquidityHub.userWalletBalanceMantissa
    ? convertMantissaToTokens({
        value: new BigNumber(apiLiquidityHub.userWalletBalanceMantissa),
        token: underlyingToken,
      })
    : undefined;

  const userVhTokenBalanceTokens = apiLiquidityHub.userHubTokenBalanceMantissa
    ? convertMantissaToTokens({
        value: new BigNumber(apiLiquidityHub.userHubTokenBalanceMantissa),
        token: vhToken,
      })
    : undefined;

  const userWalletBalanceCents = apiLiquidityHub.userWalletBalanceUsdMantissa
    ? convertUsdMantissaToCents(apiLiquidityHub.userWalletBalanceUsdMantissa)
    : userWalletBalanceTokens?.multipliedBy(tokenPriceCents);

  const userSupplyBalanceCents = apiLiquidityHub.userUnderlyingBalanceUsdMantissa
    ? convertUsdMantissaToCents(apiLiquidityHub.userUnderlyingBalanceUsdMantissa)
    : userSupplyBalanceTokens?.multipliedBy(tokenPriceCents);

  const pricePerShare = new BigNumber(apiLiquidityHub.pricePerShare);
  const supplyApyPercentage = new BigNumber(apiLiquidityHub.blendedApyRatio).multipliedBy(100);

  const yieldGroups = apiLiquidityHub.yieldGroups.map(apiYieldGroup =>
    formatToLiquidityHubYieldGroup({ apiYieldGroup, tokens, underlyingToken, tokenPriceCents }),
  );

  const { supplyTokenDistributions } = formatApiRewardDistributors({
    apiRewardDistributors: apiLiquidityHub.rewardsDistributors,
    tokens,
    supplyBalanceDollars: supplyBalanceCents.dividedBy(100),
    borrowBalanceDollars: new BigNumber(0),
  });

  const { totalApyPercentage } = getCombinedApy({
    type: 'supply',
    baseApyPercentage: supplyApyPercentage,
    tokenDistributions: supplyTokenDistributions,
  });

  const userYearlyEarningsCents = userSupplyBalanceCents
    ? calculateYearlyInterests({
        balance: userSupplyBalanceCents,
        interestPercentage: totalApyPercentage,
      })
    : undefined;

  return {
    vhToken,
    tokenPriceCents,
    tokenPriceOracleAddress: apiLiquidityHub.tokenPriceOracleAddress,
    supplyBalanceTokens,
    supplyBalanceCents,
    liquidityTokens,
    liquidityCents,
    withdrawalCapTokens,
    supplyCapTokens: convertMantissaToTokens({
      value: new BigNumber(apiLiquidityHub.supplyCapacityMantissa),
      token: underlyingToken,
    }),
    supplyApyPercentage,
    performanceFeePercentage: new BigNumber(apiLiquidityHub.performanceFeeRatio).multipliedBy(100),
    redeemFeePercentage: new BigNumber(apiLiquidityHub.redeemFeeRatio).multipliedBy(100),
    pricePerShare,
    supplierCount: apiLiquidityHub.suppliersCount ?? 0,
    yieldGroups,
    supplyTokenDistributions,
    userWalletBalanceTokens,
    userWalletBalanceCents,
    userSupplyBalanceTokens,
    userSupplyBalanceCents,
    userYearlyEarningsCents,
    userVhTokenBalanceTokens,
  };
};
